import { CompletedStandardOrder, Film, Journey, TicketType } from '@vista-digital/ocapi-types/v1'
import {
    getAllowedOrderTicketCountForShowtime,
    getFilmById,
    getFilmForShowtime,
    getItemById,
    getMemberHash,
    getShowtimeById,
    getSiteById,
    getTicketOptionsForShowtime,
    getTicketTypeById,
    isMemberSignedIn,
    loadFilmById,
    loadMember,
    loadShowtimeById,
    loadSiteById,
} from '@vista-digital/occ-domain'
import TagManager from 'react-gtm-module'
import { cmsDataService } from './cms.data.service'

export function initializeGoogleTags(gtmId: string) {
    const tagManagerArgs = {
        gtmId: gtmId,
    }

    if (gtmId && gtmId.length > 0) {
        TagManager.initialize(tagManagerArgs)
    }
}

export function sendDataLayer(dataLayerObj: DataLayer) {
    TagManager.dataLayer(dataLayerObj)
}

export async function PopulateGtmData(showtimeId): Promise<void> {
    const film = getFilmForShowtime(showtimeId)
    const showtime = getShowtimeById(showtimeId)
    const ticketsCount = getAllowedOrderTicketCountForShowtime(showtimeId)
    const site = getSiteById(showtime.siteId)

    let member

    if (isMemberSignedIn()) {
        member = await loadMember()
    }

    TagManager.dataLayer({
        dataLayer: {
            TicketQuantity: ticketsCount,
            CinemaName: site.name.text,
            performanceDate: showtime.schedule.businessDate,
            masterFilmID: film.id,
            CustomerID: member ? member.id : null,
        },
    })
}

export async function sendBookingConfirmationDataLayer(journey: Journey) {
    await loadSiteById(journey.siteId)
    const order = journey.orders[0]

    const dataLayer = new BookingConfirmationDataLayer()
    dataLayer.transactionID = order.id
    dataLayer.transactionCurrency = cmsDataService.get().isoCurrencyCode
    dataLayer.transactionAffiliation = getSiteById(journey.siteId).name.text
    dataLayer.transactionTotal = order.totalPrice.valueIncludingTax
    dataLayer.transactionTax =
        order.booking.totalBookingFee.valueIncludingTax - order.booking.totalBookingFee.valueExcludingTax
    dataLayer.transactionFee = order.booking.totalBookingFee.valueIncludingTax

    await loadShowtimeById(order.showtimes[0].showtimeId)
    const showtime = getShowtimeById(order.showtimes[0].showtimeId)

    await loadFilmById(showtime.filmId)
    const film = getFilmById(showtime.filmId)
    dataLayer.transactionODEONMasterFilmID = film.externalIds.corporateId

    dataLayer.transactionsProducts = createTransactionProducts(order, film)

    sendDataLayer(dataLayer)
}

function createTransactionProducts(order: CompletedStandardOrder, film: Film): TransactionProduct[] {
    return [...createTicketProducts(order, film), ...createItemProducts(order)]
}

function createTicketProducts(order: CompletedStandardOrder, film: Film) {
    const showtimeId = order.showtimes[0].showtimeId

    const ticketOptions = getTicketOptionsForShowtime(showtimeId)

    let ticketTypes: TicketType[] = []

    for (let t = 0; t < ticketOptions.length; t++) {
        let ticketType: TicketType = getTicketTypeById(ticketOptions[t].ticketTypeId)
        if (!ticketTypes.includes(ticketType)) {
            ticketTypes.push(ticketType)
        }
    }

    const sessionStart = getShowtimeById(showtimeId).schedule.filmStartsAt

    let ticketTypeIds: string[] = []

    order.showtimes[0].tickets.forEach(ticket => {
        if (!ticketTypeIds.includes(ticket.ticketTypeId)) {
            ticketTypeIds.push(ticket.ticketTypeId)
        }
    })

    let products: TransactionProduct[] = []

    for (let t = 0; t < ticketTypeIds.length; t++) {
        let product = new TransactionProduct()
        product.sku = ticketTypes.find(type => type.id === ticketTypeIds[t]).id
        product.category = ticketTypes.find(type => type.id === ticketTypeIds[t]).description.text
        product.name = film.title.text
        product.performanceDate = sessionStart
        product.price = order.showtimes[0].tickets
            .find(stTicket => stTicket.ticketTypeId === ticketTypeIds[t])
            .price.toString()
        product.quantity = order.showtimes[0].tickets.filter(
            stTicket => stTicket.ticketTypeId === ticketTypeIds[t]
        ).length
        products.push(product)
    }

    return products
}

function createItemProducts(order: CompletedStandardOrder) {
    const itemList = order.items.map(item => {
        return item.itemId
    })

    let itemTypes = []

    for (let t = 0; t < itemList.length; t++) {
        let itemType = getItemById(itemList[t])
        if (!itemTypes.includes(itemType)) {
            itemTypes.push(itemType)
        }
    }

    let itemIds: string[] = []

    order.items.forEach(item => {
        if (!itemIds.includes(item.itemId)) {
            itemIds.push(item.itemId)
        }
    })

    let products: TransactionProduct[] = []

    for (let i = 0; i < itemIds.length; i++) {
        let product = new TransactionProduct()

        product.sku = itemTypes.find(item => item.id === itemIds[i]).id
        product.name = itemTypes.find(item => item.id === itemIds[i]).name.text
        product.quantity = order.items.filter(item => item.itemId === itemIds[i]).length
        product.price = itemTypes.find(item => item.id === itemIds[i]).price

        products.push(product)
    }

    return products
}

export class DataLayer {
    event: string
    gtm: Gtm
    customerId: string
}

export class Gtm {
    startInTicks: number
    uniqueEventId: number
    element: string
}
export class BookingConfirmationDataLayer extends DataLayer {
    constructor() {
        super()

        this.gtm = {
            startInTicks: new Date().getTime(),
            uniqueEventId: 27,
            element: '',
        }
        this.event = 'gtm.click'
        if (isMemberSignedIn()) this.customerId = getMemberHash()
    }

    transactionID: string
    transactionAffiliation: string
    transactionTotal: number
    transactionTax: number
    transactionODEONMasterFilmID: string
    transactionCurrency: string
    transactionFee: number
    transactionsProducts: TransactionProduct[]
}

export class TransactionProduct {
    sku: string
    name: string
    category: string
    price: string
    quantity: number
    performanceDate: string
}
